/**
 * Formatting Utilities
 * Date, number, and string formatting helpers
 */

import type { FirebaseTimestamp, ReportStatus, AnimalType, Condition } from '../types'

/**
 * Format a Firebase Timestamp or Date for display
 * @param date Firebase Timestamp or Date object
 * @param options Intl.DateTimeFormatOptions
 * @returns Formatted date string
 */
export function formatDate(
  date: FirebaseTimestamp | Date | undefined | null,
  options?: Intl.DateTimeFormatOptions
): string {
  if (!date) {
    return 'Unknown date'
  }

  const d = isFirebaseTimestamp(date) ? date.toDate() : date

  const defaultOptions: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    ...options,
  }

  return d.toLocaleDateString('en-US', defaultOptions)
}

/**
 * Format date as relative time (e.g., "2 hours ago")
 * @param date Firebase Timestamp or Date
 * @returns Relative time string
 */
export function formatRelativeTime(date: FirebaseTimestamp | Date | undefined | null): string {
  if (!date) {
    return 'Unknown'
  }

  const d = isFirebaseTimestamp(date) ? date.toDate() : date
  const now = new Date()
  const diffMs = now.getTime() - d.getTime()
  const diffSec = Math.floor(diffMs / 1000)
  const diffMin = Math.floor(diffSec / 60)
  const diffHours = Math.floor(diffMin / 60)
  const diffDays = Math.floor(diffHours / 24)
  const diffWeeks = Math.floor(diffDays / 7)
  const diffMonths = Math.floor(diffDays / 30)

  if (diffSec < 60) {
    return 'Just now'
  }
  if (diffMin < 60) {
    return `${diffMin} minute${diffMin === 1 ? '' : 's'} ago`
  }
  if (diffHours < 24) {
    return `${diffHours} hour${diffHours === 1 ? '' : 's'} ago`
  }
  if (diffDays < 7) {
    return `${diffDays} day${diffDays === 1 ? '' : 's'} ago`
  }
  if (diffWeeks < 4) {
    return `${diffWeeks} week${diffWeeks === 1 ? '' : 's'} ago`
  }
  if (diffMonths < 12) {
    return `${diffMonths} month${diffMonths === 1 ? '' : 's'} ago`
  }

  return formatDate(d, { year: 'numeric', month: 'short', day: 'numeric' })
}

/**
 * Type guard for Firebase Timestamp
 */
function isFirebaseTimestamp(date: unknown): date is FirebaseTimestamp {
  return (
    typeof date === 'object' &&
    date !== null &&
    'seconds' in date &&
    'nanoseconds' in date &&
    'toDate' in date &&
    typeof (date as FirebaseTimestamp).toDate === 'function'
  )
}

/**
 * Generate a unique case ID for reports
 * Format: POM-[timestamp]-[random]
 * @returns Case ID string
 */
export function generateCaseId(): string {
  const timestamp = Date.now().toString(36).toUpperCase()
  const random = Math.random().toString(36).substring(2, 6).toUpperCase()
  return `POM-${timestamp}-${random}`
}

/**
 * Format status for display with proper capitalization
 * @param status Report status
 * @returns Formatted status string
 */
export function formatStatus(status: ReportStatus): string {
  const statusMap: Record<ReportStatus, string> = {
    pending: 'Pending Approval',
    new: 'New',
    investigating: 'Investigating',
    resolved: 'Resolved',
    closed: 'Closed',
  }
  return statusMap[status]
}

/**
 * Get CSS class for status badge
 * @param status Report status
 * @returns CSS class name
 */
export function getStatusClass(status: ReportStatus): string {
  const classMap: Record<ReportStatus, string> = {
    pending: 'badge-pending',
    new: 'badge-new',
    investigating: 'badge-investigating',
    resolved: 'badge-resolved',
    closed: 'badge-closed',
  }
  return classMap[status]
}

/**
 * Format animal type for display
 * @param type Animal type
 * @returns Formatted animal type string
 */
export function formatAnimalType(type: AnimalType): string {
  const typeMap: Record<AnimalType, string> = {
    dog: 'Dog',
    cat: 'Cat',
    bird: 'Bird',
    other: 'Other',
  }
  return typeMap[type]
}

/**
 * Get icon name for animal type (Material Icons)
 * @param type Animal type
 * @returns Material icon name
 */
export function getAnimalIcon(type: AnimalType): string {
  const iconMap: Record<AnimalType, string> = {
    dog: 'pets',
    cat: 'pets',
    bird: 'flutter_dash',
    other: 'cruelty_free',
  }
  return iconMap[type]
}

/**
 * Format condition for display
 * @param condition Animal condition
 * @returns Formatted condition string
 */
export function formatCondition(condition: Condition): string {
  const conditionMap: Record<Condition, string> = {
    healthy: 'Healthy',
    injured: 'Injured',
    sick: 'Sick',
    unknown: 'Unknown',
  }
  return conditionMap[condition]
}

/**
 * Get color class for condition
 * @param condition Animal condition
 * @returns Tailwind color class
 */
export function getConditionColor(condition: Condition): string {
  const colorMap: Record<Condition, string> = {
    healthy: 'text-green-600',
    injured: 'text-red-600',
    sick: 'text-orange-600',
    unknown: 'text-gray-600',
  }
  return colorMap[condition]
}

/**
 * Format number with comma separators
 * @param num Number to format
 * @returns Formatted number string
 */
export function formatNumber(num: number): string {
  return num.toLocaleString('en-US')
}

/**
 * Format file size for display
 * @param bytes Size in bytes
 * @returns Formatted size string (e.g., "2.5 MB")
 */
export function formatFileSize(bytes: number): string {
  if (bytes === 0) {
    return '0 B'
  }

  const k = 1024
  const sizes = ['B', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))

  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`
}

/**
 * Pluralize a word based on count
 * @param count Number
 * @param singular Singular form
 * @param plural Plural form (defaults to singular + 's')
 * @returns Pluralized string with count
 */
export function pluralize(count: number, singular: string, plural?: string): string {
  const word = count === 1 ? singular : (plural ?? `${singular}s`)
  return `${formatNumber(count)} ${word}`
}

/**
 * Format coordinates for display
 * @param lat Latitude
 * @param lng Longitude
 * @param precision Decimal places (default 6)
 * @returns Formatted coordinates string
 */
export function formatCoordinates(lat: number, lng: number, precision = 6): string {
  const latDir = lat >= 0 ? 'N' : 'S'
  const lngDir = lng >= 0 ? 'E' : 'W'

  return `${Math.abs(lat).toFixed(precision)}° ${latDir}, ${Math.abs(lng).toFixed(precision)}° ${lngDir}`
}

/**
 * Material Icons name → POM sprite ID lookup.
 *
 * The legacy `getAnimalIcon()` and other consumer call sites still hand back
 * Material Icons strings (those return values are test-locked — see decisions
 * D1, D7). Consumers translate at the call site by indexing this map. New code
 * should reference sprite IDs directly via `<svg><use href="…#i-…"/></svg>`.
 *
 * Decisions encoded:
 *   - D1: `flutter_dash` (bird) → `i-pets` (no `i-bird` in the sprite).
 *   - D3: `track_changes` → `i-schedule`.
 *   - D4: `warning` and `priority_high` → `i-error`.
 *   - D5: `near_me` → `i-my-location`; `add_circle` → `i-add`;
 *         `edit` → `i-edit-note`.
 */
export const materialToSprite: Record<string, string> = {
  // Animal types (D1 — bird falls back to generic pets glyph).
  pets: 'i-pets',
  flutter_dash: 'i-pets',
  cruelty_free: 'i-cruelty-free',

  // Status / feedback.
  check_circle: 'i-check-circle',
  check: 'i-check',
  error: 'i-error',
  warning: 'i-error',
  priority_high: 'i-error',
  info: 'i-info',
  help: 'i-help',

  // Location / map.
  location_on: 'i-location-on',
  edit_location: 'i-edit-location',
  my_location: 'i-my-location',
  near_me: 'i-my-location',
  map: 'i-map',
  view_list: 'i-view-list',
  directions: 'i-directions',

  // Photos & forms.
  photo: 'i-photo-camera',
  photo_camera: 'i-photo-camera',
  add_a_photo: 'i-add-a-photo',
  description: 'i-description',
  edit: 'i-edit-note',
  edit_note: 'i-edit-note',

  // Generic actions.
  add: 'i-add',
  add_circle: 'i-add',
  close: 'i-close',
  send: 'i-send',
  refresh: 'i-refresh',
  search: 'i-search',
  touch_app: 'i-touch-app',
  schedule: 'i-schedule',
  track_changes: 'i-schedule',
  tag: 'i-tag',

  // Admin / utility.
  menu: 'i-menu',
  dashboard: 'i-dashboard',
  pending: 'i-pending',
  pending_actions: 'i-pending-actions',
  fiber_new: 'i-fiber-new',
  archive: 'i-archive',
  summarize: 'i-summarize',
  visibility: 'i-visibility',
  delete: 'i-delete',
  inbox: 'i-inbox',
  block: 'i-block',
  arrow_back: 'i-arrow-back',
  logout: 'i-logout',
}
